import Tooltip from '@mui/material/Tooltip';
import { Clock, DollarSign, Folder, Users } from 'lucide-react';
import React from 'react';
import { displayDurationShort } from 'shared/lib/formatDuration';

import {
  DashboardHeroStatCard,
  DashboardHeroStatIcon,
  DashboardHeroStatLabel,
  DashboardHeroStatSub,
  DashboardHeroStatValue,
  DashboardHeroStatValueRow,
} from '../dashboard.styles';
import type { DashboardTopItem } from '../types/dashboard.types';
import { formatHoursMinutes } from '../utils/dashboardUtils';

interface Props {
  totalSecs: number;
  billableSecs: number;
  billPct: number;
  nonBillSecs: number;
  entryCount?: number;
  topProject?: DashboardTopItem & { totalSecs?: number };
  topClient?: DashboardTopItem & { totalSecs?: number };
}

const DashboardStatsBar: React.FC<Props> = ({
  totalSecs,
  billableSecs,
  billPct,
  nonBillSecs,
  entryCount,
  topProject,
  topClient,
}) => {
  return (
    <>
      <DashboardHeroStatCard>
        <DashboardHeroStatLabel>Tracked</DashboardHeroStatLabel>
        <DashboardHeroStatValueRow>
          <DashboardHeroStatIcon>
            <Clock size={15} />
          </DashboardHeroStatIcon>
          <DashboardHeroStatValue>
            {displayDurationShort(formatHoursMinutes(totalSecs))}
          </DashboardHeroStatValue>
        </DashboardHeroStatValueRow>
        {entryCount != null && <DashboardHeroStatSub>{entryCount} sessions</DashboardHeroStatSub>}
      </DashboardHeroStatCard>

      <DashboardHeroStatCard>
        <DashboardHeroStatLabel>Billable</DashboardHeroStatLabel>
        <DashboardHeroStatValueRow>
          <DashboardHeroStatIcon>
            <DollarSign size={15} />
          </DashboardHeroStatIcon>
          <DashboardHeroStatValue>
            {displayDurationShort(formatHoursMinutes(billableSecs))}
          </DashboardHeroStatValue>
        </DashboardHeroStatValueRow>
        <DashboardHeroStatSub>{billPct}% of total</DashboardHeroStatSub>
      </DashboardHeroStatCard>

      <DashboardHeroStatCard>
        <DashboardHeroStatLabel>Non-billable</DashboardHeroStatLabel>
        <DashboardHeroStatValueRow>
          <DashboardHeroStatIcon>
            <Clock size={15} />
          </DashboardHeroStatIcon>
          <DashboardHeroStatValue>
            {displayDurationShort(formatHoursMinutes(nonBillSecs))}
          </DashboardHeroStatValue>
        </DashboardHeroStatValueRow>
        <DashboardHeroStatSub>{100 - billPct}% of total</DashboardHeroStatSub>
      </DashboardHeroStatCard>

      {topProject && (
        <DashboardHeroStatCard>
          <DashboardHeroStatLabel>Top Project</DashboardHeroStatLabel>
          <DashboardHeroStatValueRow>
            <DashboardHeroStatIcon>
              <Folder size={15} />
            </DashboardHeroStatIcon>
            <DashboardHeroStatValue
              sx={{ fontSize: 15, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.3 }}
            >
              {topProject.name}
            </DashboardHeroStatValue>
          </DashboardHeroStatValueRow>
          {topProject.totalSecs != null && (
            <DashboardHeroStatSub>
              {displayDurationShort(formatHoursMinutes(topProject.totalSecs))}
            </DashboardHeroStatSub>
          )}
        </DashboardHeroStatCard>
      )}

      {topClient && (
        <DashboardHeroStatCard>
          <DashboardHeroStatLabel>Top Client</DashboardHeroStatLabel>
          <DashboardHeroStatValueRow>
            <DashboardHeroStatIcon>
              <Users size={15} />
            </DashboardHeroStatIcon>
            <Tooltip title={topClient.name} placement="top">
              <DashboardHeroStatValue
                sx={{
                  fontSize: 15,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  lineHeight: 1.3,
                }}
              >
                {topClient.name}
              </DashboardHeroStatValue>
            </Tooltip>
          </DashboardHeroStatValueRow>
          {topClient.totalSecs != null && (
            <DashboardHeroStatSub>
              {displayDurationShort(formatHoursMinutes(topClient.totalSecs))}
            </DashboardHeroStatSub>
          )}
        </DashboardHeroStatCard>
      )}
    </>
  );
};

export default DashboardStatsBar;
